import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  List,
  ListItem,
  Chip,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  CreateNewFolder as CreateFolderIcon,
  Upload as UploadIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  DriveFileMove as MoveIcon,
} from '@mui/icons-material';
import api, { deviceAPI, assignmentAPI, fileAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useIsAdmin } from '../utils/useIsAdmin';

interface FileNode {
  id: string;
  name: string;
  type: 'file' | 'folder';
  path: string;
  size?: number;
  mimeType?: string;
  category?: string;
  children?: FileNode[];
  isExpanded?: boolean;
  created_at?: string;
  updated_at?: string;
}

interface Device {
  id: string;
  name: string;
  label?: string | null;
  deviceId: string;
  status: string;
}

const Files = () => {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const [files, setFiles] = useState<FileNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<string>('/');
  const [sortBy, setSortBy] = useState<'name' | 'type' | 'created' | 'updated'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    item: FileNode | null;
  } | null>(null);
  const [createFolderDialog, setCreateFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);
  const [dragOverDevice, setDragOverDevice] = useState<string | null>(null);
  const [dragOverDeviceList, setDragOverDeviceList] = useState<boolean>(false);
  const [draggedFile, setDraggedFile] = useState<FileNode | null>(null);
  const [openingFile, setOpeningFile] = useState<string | null>(null);
  const [renameDialog, setRenameDialog] = useState<{
    open: boolean;
    item: FileNode | null;
    newName: string;
  }>({
    open: false,
    item: null,
    newName: ''
  });
  const [moveDialog, setMoveDialog] = useState<{
    open: boolean;
    item: FileNode | null;
    targetPath: string;
  }>({
    open: false,
    item: null,
    targetPath: ''
  });
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [deviceAssignments, setDeviceAssignments] = useState<Record<string, any[]>>({});
  const [loadingAssignments, setLoadingAssignments] = useState<Record<string, boolean>>({});
  const [deviceRenameDialog, setDeviceRenameDialog] = useState<{
    open: boolean;
    assignment: any | null;
    newName: string;
  }>({
    open: false,
    assignment: null,
    newName: ''
  });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    item: FileNode | null;
  }>({
    open: false,
    item: null
  });
  const [reviseDialog, setReviseDialog] = useState<{
    open: boolean;
    item: FileNode | null;
  }>({
    open: false,
    item: null
  });
  const [deviceFileContextMenu, setDeviceFileContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    assignment: any | null;
  } | null>(null);

  const fetchFiles = useCallback(async () => {
    try {
      setLoading(true);
      // Use the filesystem scanning endpoint for path-based navigation
      const apiPath = `/api/files/scan/${currentPath === '/' ? '' : currentPath}`;
      const response = await api.get(apiPath);

      setFiles(response.data.data || []);
    } catch (err: any) {
      console.error('API error:', err);
      console.error('Error response:', err.response);
      setError(err.response?.data?.message || t('files.failedToFetchFiles'));
    } finally {
      setLoading(false);
    }
  }, [currentPath, t]);

  const fetchDevices = useCallback(async () => {
    try {
      setLoadingDevices(true);
      const response = await deviceAPI.getAllDevices();
      setDevices(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (err: any) {
      console.error('Error fetching devices:', err);
      setError(err.response?.data?.message || t('files.failedToFetchDevices'));
    } finally {
      setLoadingDevices(false);
    }
  }, [t]);

  useEffect(() => {
    fetchFiles();
    fetchDevices();
  }, [fetchFiles, fetchDevices]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    try {
      await api.post('/api/files/folders', {
        name: newFolderName,
        path: currentPath
      });
      setCreateFolderDialog(false);
      setNewFolderName('');
      fetchFiles();
    } catch (err: any) {
      console.error('Create folder error:', err);
      console.error('Error response:', err.response);
      setError(err.response?.data?.message || t('files.failedToCreateFolder'));
    }
  };

  const handleDeviceSelect = async (deviceId: string) => {
    const newSelection = selectedDevices.includes(deviceId)
      ? selectedDevices.filter(id => id !== deviceId)
      : [...selectedDevices, deviceId];
    
    setSelectedDevices(newSelection);
    
    // Fetch assignments for all selected devices
    if (newSelection.length > 0) {
      await Promise.all(newSelection.map(id => fetchDeviceAssignments(id)));
    } else {
      setDeviceAssignments({});
      setLoadingAssignments({});
    }
  };

  const fetchDeviceAssignments = async (deviceUuid: string) => {
    try {
      setLoadingAssignments(prev => ({ ...prev, [deviceUuid]: true }));
      const device = devices.find(d => d.id === deviceUuid);
      if (device) {
        const response = await assignmentAPI.getDeviceAssignments(device.deviceId);
        const assignments = Array.isArray(response.data) ? response.data : [];
        setDeviceAssignments(prev => ({ ...prev, [deviceUuid]: assignments }));
      }
    } catch (err: any) {
      console.error('Error fetching device assignments:', err);
      setError(err.response?.data?.message || t('files.failedToFetchAssignments'));
      setDeviceAssignments(prev => ({ ...prev, [deviceUuid]: [] }));
    } finally {
      setLoadingAssignments(prev => ({ ...prev, [deviceUuid]: false }));
    }
  };

  const handleRemoveDeviceFile = async (assignmentId: string) => {
    try {
      // Only delete the assignment, not the file
      await assignmentAPI.deleteAssignment(assignmentId);
      setSuccess(t('files.fileRemovedFromDevice'));
      // Refresh assignments for all selected devices
      if (selectedDevices.length > 0) {
        await Promise.all(selectedDevices.map(id => fetchDeviceAssignments(id)));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('files.failedToRemoveFile'));
      console.error('Error removing device file assignment:', err);
    }
  };

  const handleDownloadDeviceFile = async (assignment: any) => {
    const { fileId, file_name, file_relativePath, file_mimeType } = assignment;
    try {
      const response = await api.get(`/api/files/${fileId}/download?filePath=${encodeURIComponent(file_relativePath)}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: file_mimeType || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      // Open file in new tab/window based on file type
      if (file_mimeType?.startsWith('image/') || file_mimeType?.startsWith('text/') || file_mimeType === 'application/pdf') {
        // Open in new tab for viewable files
        window.open(url, '_blank');
      } else {
        // Download for other file types
        const downloadLink = document.createElement('a');
        downloadLink.href = url;
        downloadLink.download = file_name;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
    } catch (err: any) {
      console.error('Error downloading file:', err);
      setError(err.response?.data?.message || t('files.failedToDownloadFile'));
    }
  };

  const handleRenameDeviceFile = async () => {
    if (!deviceRenameDialog.assignment || !deviceRenameDialog.newName.trim()) {
      return;
    }

    try {
      const { assignment } = deviceRenameDialog;
      // Rename the file
      await api.put(`/api/files/${assignment.file_id}/rename`, {
        newName: deviceRenameDialog.newName.trim(),
        filePath: assignment.file_relativePath || assignment.resourcePath
      });
      setSuccess(t('files.fileRenamed'));
      setDeviceRenameDialog({ open: false, assignment: null, newName: '' });
      // Refresh assignments for all selected devices
      if (selectedDevices.length > 0) {
        await Promise.all(selectedDevices.map(id => fetchDeviceAssignments(id)));
      }
      // Refresh file list to show updated name
      fetchFiles();
    } catch (err: any) {
      setError(err.response?.data?.message || t('files.failedToRenameFile'));
      console.error('Error renaming device file:', err);
    }
  };

  const handleFileDropOnDevice = async (fileId: string, deviceIds: string[]) => {
    try {
      if (deviceIds.length === 0) {
        return;
      }

      // Use bulk assignments if multiple devices, single assignment if one device
      if (deviceIds.length === 1) {
        const assignmentData = {
          deviceId: deviceIds[0],
          fileId,
          resourceType: 'FILE' as const,
        };

        await assignmentAPI.createAssignment(assignmentData);
        setSuccess(t('files.fileAssignedToDevice'));
        
        // Refresh device assignments if exactly one device is selected
        if (selectedDevices.length === 1 && selectedDevices[0] === deviceIds[0]) {
          await fetchDeviceAssignments(deviceIds[0]);
        }
      } else {
        // Bulk assignment for multiple devices
        const assignmentData = {
          assignments: deviceIds.map(deviceId => ({
            deviceId,
            fileId,
            resourceType: 'FILE' as const,
          })),
        };

        await assignmentAPI.createBulkAssignments(assignmentData);
        setSuccess(t('files.fileAssignedToDevices', { count: deviceIds.length }));
      }
    } catch (err: any) {
      console.error('Error creating assignment:', err);
      setError(err.response?.data?.message || t('files.failedToAssignFile'));
    }
  };

  const handleFolderDropOnDevice = async (folderPath: string, deviceIds: string[]) => {
    try {
      if (deviceIds.length === 0) {
        return;
      }

      // Get all files within the folder recursively (pass only the folder path, not the API path)
      const folderPathForAPI = folderPath.replace(/^\//, ''); // Remove leading slash
      const response = await fileAPI.getAllFilesInFolder(folderPathForAPI);
      const folderFiles = response.data?.data || [];

      // Filter to only files (not folders)
      const files = folderFiles.filter((file: any) => file.type === 'file');

      if (files.length === 0) {
        setError(t('files.noFilesInFolder'));
        return;
      }

      // Get file IDs from the response
      const fileIds = files.map((file: any) => file.id).filter((id: string) => id);

      if (fileIds.length === 0) {
        setError(t('files.noValidFilesInFolder'));
        return;
      }

      // Create bulk assignments for all files to all devices
      const assignmentData = {
        assignments: fileIds.flatMap((fileId: string) =>
          deviceIds.map(deviceId => ({
            deviceId,
            fileId,
            resourceType: 'FILE' as const,
          }))
        ),
      };

      await assignmentAPI.createBulkAssignments(assignmentData);
      setSuccess(t('files.folderFilesAssignedToDevices', { 
        fileCount: fileIds.length, 
        deviceCount: deviceIds.length 
      }));

      // Refresh device assignments for all selected devices
      if (selectedDevices.length > 0) {
        await Promise.all(selectedDevices.map(id => fetchDeviceAssignments(id)));
      }
    } catch (err: any) {
      console.error('Error assigning folder files to devices:', err);
      setError(err.response?.data?.message || t('files.failedToAssignFolderFiles'));
    }
  };

  const handleFileUpload = async (files: FileList, targetPath: string) => {
    const formData = new FormData();
    formData.append('path', targetPath);
    
    Array.from(files).forEach((file) => {
      formData.append('files', file);
    });

    try {
      const response = await api.post('/api/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const uploadedFiles = response.data?.data || [];
      
      // Determine which devices to assign to
      const devicesToAssign: string[] = [];
      
      // Check if upload is to a device folder (or inside a device folder)
      // Device folders are at the root level, so check the first path segment
      const pathParts = targetPath === '/' ? [] : targetPath.split('/').filter(Boolean);
      const rootFolderName = pathParts.length > 0 ? pathParts[0] : '';
      
      // Find device that matches this folder name
      const matchingDevice = devices.find(device => {
        const deviceFolderName = device.label 
          ? `${device.label} — ${device.name}`
          : device.name;
        return deviceFolderName === rootFolderName;
      });
      
      if (matchingDevice) {
        // Upload is to a device folder, assign to that device
        devicesToAssign.push(matchingDevice.id);
      } else if (selectedDevices.length > 0) {
        // Upload is not to a device folder, use selected devices
        devicesToAssign.push(...selectedDevices);
      }
      
      // Create assignments if we have devices to assign to
      if (devicesToAssign.length > 0 && uploadedFiles.length > 0) {
        try {
          const assignmentData = {
            assignments: uploadedFiles.flatMap((file: any) => 
              devicesToAssign.map(deviceId => ({
                deviceId,
                fileId: file.id,
                resourceType: 'FILE' as const,
              }))
            ),
          };
          
          await assignmentAPI.createBulkAssignments(assignmentData);
          setError(null);
          // Show success message if assignments were created
          if (uploadedFiles.length > 0 && devicesToAssign.length > 0) {
            // You can add a success state here if needed
            console.log(`Successfully assigned ${uploadedFiles.length} file(s) to ${devicesToAssign.length} device(s)`);
          }
          
          // Refresh device assignments for all assigned devices
          await Promise.all(devicesToAssign.map(id => fetchDeviceAssignments(id)));
        } catch (assignmentErr: any) {
          console.error('Error creating assignments:', assignmentErr);
          // Don't fail the upload if assignment fails, just log the error
          setError(assignmentErr.response?.data?.message || t('files.failedToCreateAssignments'));
        }
      }
      
      fetchFiles();
    } catch (err: any) {
      console.error('File upload error:', err);
      console.error('Error response:', err.response);
      setError(err.response?.data?.message || t('files.failedToUploadFiles'));
    }
  };

  const handleDeviceFileUpload = async (fileList: FileList) => {
    if (selectedDevices.length === 0) {
      return;
    }

    try {
      // For each device, ensure the device folder exists and upload files there
      const uploadedFilesByDevice: Record<string, any[]> = {};

      for (const deviceId of selectedDevices) {
        const device = devices.find(d => d.id === deviceId);
        if (!device) continue;

        // Create folder name from device label and name
        const folderName = device.label 
          ? `${device.label} — ${device.name}`
          : device.name;
        
        // The device folder path for upload (relative to uploads directory)
        const uploadPath = `${folderName}`;

        // Check if folder already exists by looking in the current files list
        const folderExists = files.some((file: FileNode) => 
          file.type === 'folder' && file.path === uploadPath
        );

        // Create folder if it doesn't exist
        if (!folderExists) {
          try {
            await fileAPI.createFolder({
              name: folderName,
              path: '/'
            });
            // Refresh files list to include the newly created folder
            await fetchFiles();
          } catch (folderErr: any) {
            const errorMessage = folderErr.response?.data?.error || folderErr.message || '';
            if (errorMessage) {
              console.error('Error creating device folder:', folderErr);
              setError(folderErr.response?.data?.message || t('files.failedToCreateFolder'));
            }
          }
        }

        // Upload files to the device folder
        const formData = new FormData();
        formData.append('path', uploadPath);
        Array.from(fileList).forEach(file => {
          formData.append('files', file);
        });

        try {
          const uploadResponse = await fileAPI.uploadFiles(formData);
          console.log('uploadResponse', JSON.stringify(uploadResponse.data, null, 2));
          const uploadedFiles = uploadResponse.data?.data || uploadResponse.data?.files || [];
          uploadedFilesByDevice[deviceId] = uploadedFiles;
        } catch (uploadErr: any) {
          console.error(`Error uploading files for device ${device.name}:`, uploadErr);
          uploadedFilesByDevice[deviceId] = [];
        }
      }

      // Create assignments for all uploaded files
      const allAssignments = Object.entries(uploadedFilesByDevice).flatMap(([deviceId, uploadedFiles]) =>
        uploadedFiles
          .filter((file: any) => file && file.id) // Filter out any invalid files
          .map((file: any) => ({
            deviceId,
            fileId: file.id,
            resourceType: 'FILE' as const,
          }))
      );

      if (allAssignments.length > 0) {
        await assignmentAPI.createBulkAssignments({
          assignments: allAssignments
        });
        setSuccess(t('files.filesUploadedAndAssigned', { count: allAssignments.length }));
        
        // Refresh assignments for all selected devices
        await Promise.all(selectedDevices.map(id => fetchDeviceAssignments(id)));
      }
      
      // Refresh file list
      fetchFiles();
    } catch (err: any) {
      console.error('Error uploading and assigning files:', err);
      setError(err.response?.data?.message || t('files.failedToUploadFiles'));
    }
  };

  const handleDragOver = (e: React.DragEvent, path: string) => {
    e.preventDefault();
    setDragOver(path);
  };

  const handleFolderDragOver = (e: React.DragEvent, folderPath: string) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent bubbling to parent
    setDragOverFolder(folderPath);
  };

  const handleDrop = (e: React.DragEvent, targetPath: string) => {
    e.preventDefault();
    setDragOver(null);

    // Only handle external file uploads (files from PC)
    if (e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files, targetPath);
    }
    // Internal moves are handled by individual folder drop zones
  };

  const handleFolderDrop = (e: React.DragEvent, folderPath: string) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent bubbling to parent drop zone
    setDragOverFolder(null);

    // Handle internal file moves (files dragged within the app)
    const sourcePath = e.dataTransfer.getData('text/plain');
    if (sourcePath && sourcePath !== folderPath) {
      handleMoveFile(sourcePath, folderPath);
    } else {
      console.error('Invalid folder drop: source path is missing or same as target');
    }
  };

  const handleMoveFile = async (sourcePath: string, targetPath: string) => {
    try {
      // Check if source and target are the same (prevent moving to same location)
      if (sourcePath === targetPath) {
        console.warn('Source and target are the same, skipping move');
        return;
      }
      
      // Extract the filename from the source path
      const fileName = sourcePath.split('/').pop() || '';
      const newTargetPath = targetPath === '/' ? `/${fileName}` : `${targetPath}/${fileName}`;
      
      // Check if we're trying to move to the same location
      if (sourcePath === newTargetPath) {
        console.warn('Target path would be the same as source, skipping move');
        return;
      }
      
      await api.put('/api/files/move', {
        sourcePath,
        targetPath: newTargetPath
      });
      
      fetchFiles();
    } catch (err: any) {
      console.error('Move file error:', err);
      setError(err.response?.data?.message || t('files.failedToMoveFile'));
    }
  };

  const handleMoveItem = async (item: FileNode, targetPath: string) => {
    try {
      // Extract the filename from the source path
      const fileName = item.path.split('/').pop() || '';
      const newTargetPath = targetPath === '/' ? `/${fileName}` : `${targetPath}/${fileName}`;
      
      await api.put('/api/files/move', {
        sourcePath: item.path,
        targetPath: newTargetPath
      });
      
      setMoveDialog({ open: false, item: null, targetPath: '' });
      fetchFiles();
    } catch (err: any) {
      console.error('Move item error:', err);
      setError(err.response?.data?.message || t('files.failedToMoveFile'));
    }
  };

  const handleCopyFile = async (item: FileNode) => {
    try {
        await api.post('/api/files/copy', {
          sourcePath: item.path
        });
      
      fetchFiles();
    } catch (err: any) {
      console.error('Copy file error:', err);
      setError(err.response?.data?.message || t('files.failedToMoveFile'));
    }
  };

  const handleDragStart = (e: React.DragEvent, item: FileNode) => {
    // Store file data for device assignment
    if (item.type === 'file') {
      e.dataTransfer.setData('text/plain', item.path);
      e.dataTransfer.setData('application/json', JSON.stringify({ fileId: item.id, path: item.path, type: 'file' }));
      e.dataTransfer.effectAllowed = 'all'; // Allow both copy (for devices) and move (for folders)
      setDraggedFile(item);
    } else {
      // For folders, store folder data for both file system operations and device assignment
      e.dataTransfer.setData('text/plain', item.path);
      e.dataTransfer.setData('application/json', JSON.stringify({ folderPath: item.path, type: 'folder' }));
      e.dataTransfer.effectAllowed = 'all'; // Allow both copy (for devices) and move (for folders)
      setDraggedFile(item); // Store folder info for device assignment
    }

    // Set drag image if needed
    if (e.target instanceof HTMLElement) {
      e.dataTransfer.setDragImage(e.target, 0, 0);
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    e.preventDefault();

    setDragOver(null);
    setDragOverFolder(null);
  };

  const handleContextMenu = (event: React.MouseEvent, item: FileNode) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      item,
    });
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const handleDeviceFileContextMenu = (event: React.MouseEvent, assignment: any) => {
    event.preventDefault();
    event.stopPropagation();
    setDeviceFileContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      assignment,
    });
  };

  const handleDeviceFileContextMenuClose = () => {
    setDeviceFileContextMenu(null);
  };

  const handleFileClick = async (file: FileNode) => {
    if (file.type === 'file') {
      try {
        setOpeningFile(file.id);
        
        const response = await api.get(`/api/files/${file.id}/download?filePath=${encodeURIComponent(file.path)}`, {
          responseType: 'blob'
        });
        
        // Create blob URL and open file
        const blob = new Blob([response.data], { type: file.mimeType || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        
        // Open file in new tab/window based on file type
        if (file.mimeType?.startsWith('image/') || file.mimeType?.startsWith('text/') || file.mimeType === 'application/pdf') {
          // Open in new tab for viewable files
          window.open(url, '_blank');
        } else {
          // Download for other file types
          const downloadLink = document.createElement('a');
          downloadLink.href = url;
          downloadLink.download = file.name;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        }
        
        // Clean up blob URL
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error opening file:', error);
        setError(t('files.failedToOpenFile'));
      } finally {
        setOpeningFile(null);
      }
    }
  };

  const handleRename = async (item: FileNode, newName: string) => {
    try {
      if (item.type === 'file') {
        await api.put(`/api/files/${item.id}/rename`, { 
          newName,
          filePath: item.path 
        });
      } else {
        await api.put('/api/files/folders/rename', { 
          oldPath: item.path, 
          newName 
        });
      }
      setRenameDialog({ open: false, item: null, newName: '' });
      fetchFiles();
      // Refresh device files view for all selected devices
      if (selectedDevices.length > 0) {
        await Promise.all(selectedDevices.map(id => fetchDeviceAssignments(id)));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('files.failedToRenameItem'));
    }
  };

  const handleDeleteItem = async (item: FileNode) => {
    try {
      if (item.type === 'file') {
        await api.delete(`/api/files/${item.id}`, {
          data: { filePath: item.path }
        });
      } else {
        await api.delete('/api/files/folders', { 
          data: { folderPath: item.path } 
        });
      }
      fetchFiles();
      // Refresh device files view for all selected devices
      if (selectedDevices.length > 0) {
        await Promise.all(selectedDevices.map(id => fetchDeviceAssignments(id)));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('files.failedToDeleteItem'));
    }
  };

  const handleReviseFile = async (item: FileNode, file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('filePath', item.path);

      await fileAPI.reviseFile(item.id, formData);
      setSuccess(t('files.fileRevised'));
      setReviseDialog({ open: false, item: null });
      fetchFiles();
      // Refresh device files view for all selected devices
      if (selectedDevices.length > 0) {
        await Promise.all(selectedDevices.map(id => fetchDeviceAssignments(id)));
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || t('files.failedToReviseFile'));
    }
  };

  const renderFileIcon = (item: FileNode, size: 'small' | 'medium' | 'large' = 'medium') => {
    const iconSize = size === 'small' ? 20 : size === 'medium' ? 24 : 48;
    if (item.type === 'folder') {
      return <FolderIcon sx={{ color: 'primary.main', fontSize: iconSize, mr: 1 }} />;
    }
    
    // File type icons based on category
    switch (item.category) {
      case 'DOCUMENT':
        return <FileIcon className="file-icon" sx={{ color: 'error.main', fontSize: iconSize, mr: 1 }} />;
      default:
        return <FileIcon className="file-icon" sx={{ color: 'text.secondary', fontSize: iconSize, mr: 1 }} />;
    }
  };

  const handleSort = (column: 'name' | 'type' | 'created' | 'updated') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return '-';
    }
  };

  const sortedFiles = [...files].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'type':
        comparison = a.type.localeCompare(b.type);
        if (comparison === 0) {
          comparison = a.name.localeCompare(b.name);
        }
        break;
      case 'created':
        const aCreated = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bCreated = b.created_at ? new Date(b.created_at).getTime() : 0;
        comparison = aCreated - bCreated;
        if (comparison === 0) {
          comparison = a.name.localeCompare(b.name);
        }
        break;
      case 'updated':
        const aUpdated = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const bUpdated = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        comparison = aUpdated - bUpdated;
        if (comparison === 0) {
          comparison = a.name.localeCompare(b.name);
        }
        break;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const renderBreadcrumbs = () => {
    const pathParts = currentPath.split('/').filter(Boolean);
    const breadcrumbs = [
      { name: 'Home', path: '/' },
      ...pathParts.map((part, index) => ({
        name: part,
        path: '/' + pathParts.slice(0, index + 1).join('/')
      }))
    ];

    return (
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ 
          '& .MuiBreadcrumbs-ol': {
            flexWrap: { xs: 'wrap', md: 'nowrap' }
          }
        }}
      >
        {breadcrumbs.map((crumb, index) => (
          <Link
            key={crumb.path}
            color={index === breadcrumbs.length - 1 ? 'text.primary' : 'inherit'}
            onClick={() => setCurrentPath(crumb.path)}
            sx={{ 
              cursor: 'pointer', 
              textDecoration: 'none',
              fontSize: { xs: '0.875rem', md: '1rem' },
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {index === 0 ? <HomeIcon sx={{ mr: 0.5, fontSize: { xs: '1rem', md: '1.25rem' } }} /> : null}
            {crumb.name}
          </Link>
        ))}
      </Breadcrumbs>
    );
  };

  function isTruncated(item: FileNode): boolean {
    const width = window.innerWidth;
    let maxLength: number;
  
    if (item.type === 'folder') return false;

    if (width >= 900 && width <= 1200) {
      maxLength = 30;

      return item.name.length > maxLength;
    }

    return false;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, md: 0 } }}>
      <Box mb={{ xs: 2, md: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>{t('files.title')}</Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Three Column Layout: File List (Left), Device Selection (Middle), Device Files (Right) */}
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ alignItems: 'flex-start', maxWidth: '100%', width: '100%', mx: 'auto' }}>
          {/* Left Column - File List (25% bigger than other columns) */}
          <Grid item xs={12} sm={12} md={12} lg={12} xl={6} sx={{ minWidth: 0, width: '100%' }}>
            <Box sx={{ mt: { xs: 1, md: 2 }, height: { xs: '450px', sm: '500px', md: '600px', lg: '650px' }, width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Paper
              sx={{
                height: '100%',
                p: { xs: 1.5, sm: 2 },
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: dragOver ? 'action.hover' : 'background.paper',
                border: dragOver ? '2px dashed' : '1px solid',
                borderColor: dragOver ? 'primary.main' : 'divider',
                transition: 'all 0.2s ease-in-out',
                position: 'relative',
                '&::before': dragOver ? {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'primary.main',
                  opacity: 0.1,
                  zIndex: 0,
                  pointerEvents: 'none'
                } : {},
                '&::after': dragOver ? {
                  content: `"${t('files.dropFilesHere')}"`,
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'primary.main',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  zIndex: 1,
                  pointerEvents: 'none'
                } : {}
              }}
              onDragOver={(e: React.DragEvent) => handleDragOver(e, currentPath)}
              onDrop={(e: React.DragEvent) => handleDrop(e, currentPath)}
              onDragLeave={(e: React.DragEvent) => {
                e.preventDefault();
                setDragOver(null);
              }}
      >
        <Box mb={{ xs: 1.5, md: 2 }}>
          {renderBreadcrumbs()}
        </Box>
        {files.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            flex={1}
            color="text.secondary"
          >
            <UploadIcon sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('files.noFilesInFolder')}
            </Typography>
            <Typography variant="body2">
              {t('files.dragDropInstructions')}
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ 
            flex: 1, 
            overflow: 'auto', 
            display: 'block', 
            width: '100%',
            '&::-webkit-scrollbar': {
              height: '8px',
              width: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,.2)',
              borderRadius: '4px',
            },
          }}>
            <Table sx={{ minWidth: { xs: 400, sm: 500, md: 'auto' }, width: '100%', tableLayout: { xs: 'fixed', md: 'auto' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' }, 
                    width: { xs: '50%', sm: '45%', md: 'auto' }, 
                    minWidth: { xs: '120px', sm: '150px', md: 'auto' },
                    pl: { xs: 1, sm: 2 },
                    pr: { xs: 0.5, sm: 1 }
                  }}>
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction={sortBy === 'name' ? sortOrder : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      {t('files.name')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' }, 
                    display: { xs: 'none', sm: 'table-cell' }, 
                    width: { sm: '25%', md: 'auto' },
                    px: { sm: 1, md: 2 }
                  }}>
                    <TableSortLabel
                      active={sortBy === 'type'}
                      direction={sortBy === 'type' ? sortOrder : 'asc'}
                      onClick={() => handleSort('type')}
                    >
                      {t('files.type')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' }, 
                    display: { xs: 'none', md: 'table-cell' }, 
                    width: { md: 'auto' },
                    px: { md: 1, lg: 2 }
                  }}>
                    <TableSortLabel
                      active={sortBy === 'created'}
                      direction={sortBy === 'created' ? sortOrder : 'asc'}
                      onClick={() => handleSort('created')}
                    >
                      {t('files.created')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' }, 
                    display: { xs: 'none', lg: 'table-cell' }, 
                    width: { lg: 'auto' },
                    px: { lg: 1, xl: 2 }
                  }}>
                    <TableSortLabel
                      active={sortBy === 'updated'}
                      direction={sortBy === 'updated' ? sortOrder : 'asc'}
                      onClick={() => handleSort('updated')}
                    >
                      {t('files.updated')}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Parent directory row */}
                {currentPath !== '/' && (() => {
                  const pathParts = currentPath.split('/').filter(Boolean);
                  const parentPath = pathParts.length > 1 
                    ? '/' + pathParts.slice(0, -1).join('/')
                    : '/';
                  
                  return (
                    <TableRow
                      hover
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: 'action.hover',
                        '&:hover': {
                          backgroundColor: 'action.selected',
                        },
                        ...(dragOverFolder === parentPath && {
                          border: '2px dashed',
                          borderColor: 'primary.main',
                          backgroundColor: 'primary.main',
                          opacity: 0.8,
                        })
                      }}
                      onDragOver={(e: React.DragEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleFolderDragOver(e, parentPath);
                      }}
                      onDrop={(e: React.DragEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleFolderDrop(e, parentPath);
                      }}
                      onDragLeave={(e: React.DragEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOverFolder(null);
                      }}
                      onClick={() => setCurrentPath(parentPath)}
                      onDoubleClick={() => setCurrentPath(parentPath)}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <FolderIcon sx={{ color: 'primary.main', fontSize: 24, mr: 1 }} />
                          <Typography variant="body2">..</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {t('files.folder')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })()}
                
                {sortedFiles.map((item) => (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{
                      cursor: openingFile === item.id ? 'not-allowed' : 'pointer',
                      opacity: openingFile === item.id ? 0.7 : 1,
                      ...(item.type === 'folder' && dragOverFolder === item.path && {
                        border: '2px dashed',
                        borderColor: 'primary.main',
                        backgroundColor: 'primary.main',
                        opacity: 0.8,
                      })
                    }}
                    draggable
                    onDragStart={(e: React.DragEvent) => handleDragStart(e, item)}
                    onDragEnd={(e: React.DragEvent) => handleDragEnd(e)}
                    onDragOver={(e: React.DragEvent) => {
                      if (item.type === 'folder') {
                        e.preventDefault();
                        handleFolderDragOver(e, item.path);
                      }
                    }}
                    onDrop={(e: React.DragEvent) => {
                      if (item.type === 'folder') {
                        e.preventDefault();
                        handleFolderDrop(e, item.path);
                      }
                    }}
                    onDragLeave={(e: React.DragEvent) => {
                      if (item.type === 'folder') {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOverFolder(null);
                      }
                    }}
                    onContextMenu={(e: React.MouseEvent) => handleContextMenu(e, item)}
                    onClick={async () => {
                      if (openingFile === item.id) return;
                      if (item.type === 'folder') {
                        setCurrentPath(item.path);
                      } else {
                        try {
                          await handleFileClick(item);
                        } catch (error) {
                          console.error('Error handling file click:', error);
                        }
                      }
                    }}
                    onDoubleClick={async () => {
                      if (openingFile === item.id) return;
                      if (item.type === 'folder') {
                        setCurrentPath(item.path);
                      } else {
                        try {
                          await handleFileClick(item);
                        } catch (error) {
                          console.error('Error handling file click:', error);
                        }
                      }
                    }}
                  >
                    <TableCell sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' }, 
                      width: { xs: '50%', sm: '45%', md: 'auto' }, 
                      minWidth: { xs: '120px', sm: '150px', md: 'auto' }, 
                      overflow: 'hidden',
                      pl: { xs: 1, sm: 2 },
                      pr: { xs: 0.5, sm: 1 }
                    }}>
                      <Box display="flex" alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                        {openingFile === item.id ? (
                          <CircularProgress size={20} sx={{ mr: 1, flexShrink: 0 }} />
                        ) : (
                          <Box sx={{ mr: 1, flexShrink: 0 }}>
                            {renderFileIcon(item, 'small')}
                          </Box>
                        )}
                        { isTruncated(item) ? 
                          <Tooltip title={item.name}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: item.type === 'folder' ? 500 : 400,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontSize: { xs: '0.75rem', md: '0.875rem' },
                                minWidth: 0,
                                flex: 1
                              }}
                            >
                              {(item.name.slice(0, 30) + '...')}{isTruncated(item)}
                            </Typography>
                          </Tooltip> :
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: item.type === 'folder' ? 500 : 400,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              fontSize: { xs: '0.75rem', md: '0.875rem' },
                              minWidth: 0,
                              flex: 1
                            }}
                          >
                            {item.name}
                          </Typography>
                        }
                      </Box>
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' }, 
                      display: { xs: 'none', sm: 'table-cell' }, 
                      width: { sm: '25%', md: 'auto' }, 
                      overflow: 'hidden',
                      px: { sm: 1, md: 2 }
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {item.type === 'folder' ? t('files.folder') : t('files.file')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' }, 
                      display: { xs: 'none', md: 'table-cell' }, 
                      width: { md: 'auto' }, 
                      overflow: 'hidden',
                      px: { md: 1, lg: 2 }
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {formatDate(item.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' }, 
                      display: { xs: 'none', lg: 'table-cell' }, 
                      width: { lg: 'auto' }, 
                      overflow: 'hidden',
                      px: { lg: 1, xl: 2 }
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {formatDate(item.updated_at)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
            {isAdmin && (
              <>
                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent={{ xs: 'stretch', sm: 'flex-end' }} gap={{ xs: 1, sm: 1 }} mt="auto" pt={{ xs: 1.5, md: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<CreateFolderIcon />}
                    onClick={() => setCreateFolderDialog(true)}
                    size="small"
                    sx={{ 
                      width: { xs: '100%', sm: 'auto' },
                      minWidth: { sm: '140px' },
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                    }}
                  >
                    {t('files.newFolder')}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<UploadIcon />}
                    onClick={() => document.getElementById('file-upload')?.click()}
                    size="small"
                    sx={{ 
                      width: { xs: '100%', sm: 'auto' },
                      minWidth: { sm: '140px' },
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                    }}
                  >
                    {t('files.uploadFiles')}
                  </Button>
                </Box>
                <Box component="input"
                  id="file-upload"
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.xlsm,.xlsb"
                  sx={{ display: 'none' }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.files) {
                      handleFileUpload(e.target.files, currentPath);
                    }
                  }}
                />
              </>
            )}
            </Paper>
            </Box>
          </Grid>

          {/* Middle Column - Device Selection */}
          <Grid item xs={12} sm={12} md={6} lg={6} xl={3} sx={{ minWidth: 0, width: '100%' }}>
            <Box sx={{ mt: { xs: 1, md: 2 }, height: { xs: '450px', sm: '500px', md: '600px', lg: '650px' }, width: '100%' }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', p: { xs: 1.5, md: 2 } }}>
                <Typography variant="h6" mb={2} sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                  {t('files.devices')} ({selectedDevices.length} {t('files.selected')})
                </Typography>
                {loadingDevices ? (
                  <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : devices.length > 0 ? (
                  <Paper 
                    sx={{ 
                      flex: 1, 
                      overflow: 'auto', 
                      mb: 2, 
                      minHeight: 0,
                      ...(dragOverDeviceList && {
                        border: '2px dashed',
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover',
                      })
                    }}
                    onDragOver={(e: React.DragEvent) => {
                      // Allow drop on the entire list if multiple devices are selected and a file or folder is dragged
                      if (selectedDevices.length > 1 && draggedFile && (draggedFile.type === 'file' || draggedFile.type === 'folder')) {
                        e.preventDefault();
                        e.stopPropagation();
                        e.dataTransfer.dropEffect = 'copy';
                        setDragOverDeviceList(true);
                      }
                    }}
                    onDragLeave={(e: React.DragEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Only clear if we're actually leaving the Paper component
                      const relatedTarget = e.relatedTarget as HTMLElement;
                      if (!e.currentTarget.contains(relatedTarget)) {
                        setDragOverDeviceList(false);
                      }
                    }}
                    onDrop={async (e: React.DragEvent) => {
                      // Handle drop on the entire list when multiple devices are selected
                      if (selectedDevices.length > 1) {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOverDeviceList(false);
                        
                        const fileDataStr = e.dataTransfer.getData('application/json');
                        if (fileDataStr) {
                          try {
                            const fileData = JSON.parse(fileDataStr);
                            if (fileData.type === 'file' && fileData.fileId && draggedFile && draggedFile.type === 'file') {
                              await handleFileDropOnDevice(fileData.fileId, selectedDevices);
                              setDraggedFile(null);
                            } else if (fileData.type === 'folder' && fileData.folderPath && draggedFile && draggedFile.type === 'folder') {
                              await handleFolderDropOnDevice(fileData.folderPath, selectedDevices);
                              setDraggedFile(null);
                            }
                          } catch (error) {
                            console.error('Error parsing drag data:', error);
                          }
                        }
                      }
                    }}
                  >
                    <List dense sx={{ py: { xs: 0.5, sm: 1 } }}>
                      {devices.map((device) => (
                        <ListItem 
                          key={device.id} 
                          sx={{ 
                            py: { xs: 0.5, sm: 1 },
                            px: { xs: 1, sm: 2 },
                            ...(dragOverDevice === device.id && {
                              border: '2px dashed',
                              borderColor: 'primary.main',
                              backgroundColor: 'primary.main',
                              opacity: 0.8,
                            })
                          }}
                          onDragOver={(e: React.DragEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Only allow drop on individual device if single device or no devices selected
                            // If multiple devices are selected, the Paper component handles the drop
                            if (selectedDevices.length <= 1 && draggedFile && (draggedFile.type === 'file' || draggedFile.type === 'folder')) {
                              e.dataTransfer.dropEffect = 'copy';
                              setDragOverDevice(device.id);
                            } else if (selectedDevices.length > 1) {
                              // If multiple devices selected, show visual feedback on the Paper instead
                              e.dataTransfer.dropEffect = 'copy';
                            } else {
                              e.dataTransfer.dropEffect = 'none';
                            }
                          }}
                          onDragLeave={(e: React.DragEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDragOverDevice(null);
                          }}
                          onDrop={async (e: React.DragEvent) => {
                            // Only handle individual device drop if single device or no devices selected
                            if (selectedDevices.length <= 1) {
                              e.preventDefault();
                              e.stopPropagation();
                              setDragOverDevice(null);
                              
                              // Get file or folder data from drag event
                              const fileDataStr = e.dataTransfer.getData('application/json');
                              if (fileDataStr) {
                                try {
                                  const fileData = JSON.parse(fileDataStr);
                                  // If device is selected, assign to that device, otherwise assign to the dropped device
                                  const targetDeviceId = selectedDevices.length === 1 ? selectedDevices[0] : device.id;
                                  
                                  if (fileData.type === 'file' && fileData.fileId && draggedFile && draggedFile.type === 'file') {
                                    await handleFileDropOnDevice(fileData.fileId, [targetDeviceId]);
                                    setDraggedFile(null);
                                  } else if (fileData.type === 'folder' && fileData.folderPath && draggedFile && draggedFile.type === 'folder') {
                                    await handleFolderDropOnDevice(fileData.folderPath, [targetDeviceId]);
                                    setDraggedFile(null);
                                  }
                                } catch (error) {
                                  console.error('Error parsing drag data:', error);
                                }
                              }
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: { xs: 32, sm: 40 } }}>
                            <Checkbox
                              checked={selectedDevices.includes(device.id)}
                              onChange={() => handleDeviceSelect(device.id)}
                              size="small"
                            />
                          </ListItemIcon>
                          <ListItemText 
                            primary={device.label ? `${device.label} — ${device.name}` : device.name}
                            secondary={device.deviceId}
                            primaryTypographyProps={{ 
                              fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '1rem' },
                              noWrap: true,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                            secondaryTypographyProps={{ 
                              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                              noWrap: true
                            }}
                          />
                          <Chip 
                            label={device.status} 
                            size="small" 
                            color={device.status === 'ACTIVE' ? 'success' : 'default'}
                            sx={{ fontSize: { xs: '0.6875rem', sm: '0.75rem' } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('files.noDevicesAvailable')}
                  </Typography>
                )}
                <Box sx={{ mt: 'auto' }}>
                  <Button
                    variant="contained"
                    startIcon={<UploadIcon />}
                    onClick={() => document.getElementById('device-file-upload')?.click()}
                    disabled={selectedDevices.length === 0}
                    fullWidth
                  >
                    {t('files.uploadAndAssign')}
                  </Button>
                  <Box component="input"
                    id="device-file-upload"
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.xlsm,.xlsb"
                    sx={{ display: 'none' }}
                    onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                      if (e.target.files && selectedDevices.length > 0) {
                        try {
                          await handleDeviceFileUpload(e.target.files);
                        } catch (error) {
                          console.error('Error uploading files:', error);
                        }
                        // Reset the input so the same file can be selected again
                        e.target.value = '';
                      }
                    }}
                  />
                  {selectedDevices.length > 0 && selectedDevices.length !== 1 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      {t('files.uploadedFilesWillBeAssigned', { count: selectedDevices.length })}
                    </Alert>
                  )}
                </Box>
              </CardContent>
            </Card>
            </Box>
          </Grid>

          {/* Right Column - Device Files */}
          <Grid item xs={12} sm={12} md={6} lg={6} xl={3} sx={{ minWidth: 0, width: '100%' }}>
            <Box sx={{ mt: { xs: 1, md: 2 }, height: { xs: '450px', sm: '500px', md: '600px', lg: '650px' }, width: '100%' }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', p: { xs: 1.5, md: 2 } }}>
                  <Typography variant="h6" mb={2} sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                    {t('files.deviceFiles')}
                  </Typography>
                  {selectedDevices.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {t('files.selectDeviceToViewFiles')}
                    </Typography>
                  ) : (
                    <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {selectedDevices.map((deviceId) => {
                        const device = devices.find(d => d.id === deviceId);
                        const assignments = deviceAssignments[deviceId] || [];
                        const loading = loadingAssignments[deviceId] || false;
                        
                        return (
                          <Paper 
                            key={deviceId} 
                            sx={{ 
                              flex: selectedDevices.length > 1 ? '0 0 auto' : 1,
                              display: 'flex',
                              flexDirection: 'column',
                              minHeight: selectedDevices.length > 1 ? '200px' : 0,
                              maxHeight: selectedDevices.length > 1 ? '300px' : 'none',
                              overflow: 'hidden'
                            }}
                          >
                            <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 600 }}>
                                {device ? (device.label ? `${device.label} — ${device.name}` : device.name) : 'Unknown Device'}
                              </Typography>
                            </Box>
                            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                              {loading ? (
                                <Box display="flex" justifyContent="center" p={2}>
                                  <CircularProgress size={20} />
                                </Box>
                              ) : assignments.length > 0 ? (
                                <TableContainer sx={{ 
                                  maxHeight: '100%',
                                  '&::-webkit-scrollbar': {
                                    height: '6px',
                                    width: '6px',
                                  },
                                  '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: 'rgba(0,0,0,.2)',
                                    borderRadius: '3px',
                                  },
                                }}>
                                  <Table size="small" stickyHeader>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ 
                                          fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8125rem' },
                                          pl: { xs: 1, sm: 1.5 },
                                          pr: { xs: 0.5, sm: 1 },
                                          py: 0.5
                                        }}>{t('files.name')}</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {assignments.map((assignment: any) => (
                                        <TableRow 
                                          key={assignment.id} 
                                          hover
                                          sx={{ cursor: 'pointer' }}
                                          onClick={async () => {
                                            if (assignment.file_id) {
                                              try {
                                                await handleDownloadDeviceFile(assignment);
                                              } catch (error) {
                                                console.error('Error downloading file:', error);
                                              }
                                            }
                                          }}
                                          onContextMenu={(e: React.MouseEvent) => handleDeviceFileContextMenu(e, assignment)}
                                        >
                                          <TableCell sx={{
                                            pl: { xs: 1, sm: 1.5 },
                                            pr: { xs: 0.5, sm: 1 },
                                            py: 0.5
                                          }}>
                                            <Box display="flex" alignItems="center">
                                              {assignment.file_id ? renderFileIcon({ 
                                                id: assignment.file_id, 
                                                type: 'file', 
                                                name: assignment.resourceName || assignment.file_name || '',
                                                path: assignment.file_relativePath || assignment.resourcePath || ''
                                              }, 'small') : <FileIcon fontSize="small" />}
                                              <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                  ml: 1,
                                                  fontWeight: 400,
                                                  fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8125rem' },
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  whiteSpace: 'nowrap'
                                                }}
                                              >
                                                {assignment.resourceName || assignment.file_name || assignment.file_originalName || 'Unknown'}
                                              </Typography>
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              ) : (
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  sx={{ 
                                    p: 2, 
                                    textAlign: 'center',
                                    fontSize: { xs: '0.75rem', sm: '0.8125rem' }
                                  }}
                                >
                                  {t('files.noFilesAssignedToDevice')}
                                </Typography>
                              )}
                            </Box>
                          </Paper>
                        );
                      })}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, item: null })}>
        <DialogTitle>
          {deleteDialog.item?.type === 'file' ? t('files.confirmDeleteFileTitle') : t('files.confirmDeleteFolderTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {deleteDialog.item?.type === 'file' 
              ? t('files.confirmDeleteFileMessage', { name: deleteDialog.item?.name })
              : t('files.confirmDeleteFolderMessage', { name: deleteDialog.item?.name })
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, item: null })}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={async () => {
              if (deleteDialog.item) {
                try {
                  await handleDeleteItem(deleteDialog.item);
                  setDeleteDialog({ open: false, item: null });
                } catch (error) {
                  console.error('Error deleting item:', error);
                }
              }
            }}
            variant="contained"
            color="error"
          >
            {t('files.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Device File Rename Dialog */}
      <Dialog open={deviceRenameDialog.open} onClose={() => setDeviceRenameDialog({ open: false, assignment: null, newName: '' })}>
        <DialogTitle>{t('files.renameFile')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('files.newFileName')}
            fullWidth
            variant="outlined"
            value={deviceRenameDialog.newName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
              setDeviceRenameDialog({ ...deviceRenameDialog, newName: e.target.value })
            }
            onKeyPress={async (e: React.KeyboardEvent) => {
              if (e.key === 'Enter') {
                try {
                  await handleRenameDeviceFile();
                } catch (error) {
                  console.error('Error renaming file:', error);
                }
              }
            }}
            sx={{
              minWidth: deviceRenameDialog.assignment?.resourceName 
                ? `${Math.max(deviceRenameDialog.assignment.resourceName.length * 8 + 40, 200)}px` 
                : '200px',
              width: 'auto'
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeviceRenameDialog({ open: false, assignment: null, newName: '' })}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={async () => {
              try {
                await handleRenameDeviceFile();
              } catch (error) {
                console.error('Error renaming file:', error);
              }
            }}
            variant="contained"
          >
            {t('files.rename')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog open={createFolderDialog} onClose={() => setCreateFolderDialog(false)}>
        <DialogTitle>{t('files.createNewFolder')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('files.folderName')}
            fullWidth
            variant="outlined"
            value={newFolderName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewFolderName(e.target.value)}
            onKeyPress={async (e: React.KeyboardEvent) => {
              if (e.key === 'Enter') {
                try {
                  await handleCreateFolder();
                } catch (error) {
                  console.error('Error creating folder:', error);
                }
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderDialog(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleCreateFolder} variant="contained">
            {t('files.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revise File Dialog */}
      <Dialog open={reviseDialog.open} onClose={() => setReviseDialog({ open: false, item: null })}>
        <DialogTitle>{t('files.reviseFile')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('files.reviseFileDescription', { fileName: reviseDialog.item?.name || '' })}
          </Typography>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            fullWidth
            sx={{ mt: 1 }}
          >
            {t('files.selectFileToReplace')}
            <input
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.xlsm,.xlsb"
              style={{ display: 'none' }}
              onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.files && e.target.files.length > 0 && reviseDialog.item) {
                  try {
                    await handleReviseFile(reviseDialog.item, e.target.files[0]);
                    e.target.value = ''; // Reset input
                  } catch (error) {
                    console.error('Error revising file:', error);
                  }
                }
              }}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviseDialog({ open: false, item: null })}>
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Device File Context Menu */}
      <Menu
        open={deviceFileContextMenu !== null}
        onClose={handleDeviceFileContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          deviceFileContextMenu !== null
            ? { top: deviceFileContextMenu.mouseY, left: deviceFileContextMenu.mouseX }
            : undefined
        }
      >
        {isAdmin && (
          <>
            <MenuItem 
              onClick={async () => {
                if (deviceFileContextMenu?.assignment) {
                  try {
                    setDeviceRenameDialog({
                      open: true,
                      assignment: deviceFileContextMenu.assignment,
                      newName: deviceFileContextMenu.assignment.resourceName || deviceFileContextMenu.assignment.file_name || deviceFileContextMenu.assignment.file_originalName || ''
                    });
                  } catch (error) {
                    console.error('Error opening rename dialog:', error);
                  }
                }
                handleDeviceFileContextMenuClose();
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('files.rename')}</ListItemText>
            </MenuItem>
            <MenuItem 
              onClick={async () => {
                if (deviceFileContextMenu?.assignment) {
                  try {
                    await handleRemoveDeviceFile(deviceFileContextMenu.assignment.id);
                  } catch (error) {
                    console.error('Error removing file assignment:', error);
                  }
                }
                handleDeviceFileContextMenuClose();
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('files.remove')}</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {contextMenu?.item?.type === 'file' && (
          <MenuItem 
            onClick={async () => {
              if (contextMenu?.item) {
                try {
                  await handleFileClick(contextMenu.item);
                } catch (error) {
                  console.error('Error handling file click:', error);
                }
              }
              handleContextMenuClose();
            }}
            disabled={openingFile === contextMenu.item.id}
          >
            <ListItemIcon>
              <FileIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {openingFile === contextMenu.item.id ? t('files.opening') : t('files.open')}
            </ListItemText>
          </MenuItem>
        )}
        {isAdmin && (
          <>
            <MenuItem onClick={() => {
              if (contextMenu?.item) {
                setRenameDialog({
                  open: true,
                  item: contextMenu.item,
                  newName: contextMenu.item.name
                });
              }
              handleContextMenuClose();
            }}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('files.rename')}</ListItemText>
            </MenuItem>
            {contextMenu?.item?.type === 'file' && (
            <MenuItem onClick={async () => {
              if (contextMenu?.item) {
                try {
                  await handleCopyFile(contextMenu.item);
                } catch (error) {
                  console.error('Error copying file:', error);
                }
              }
              handleContextMenuClose();
            }}>
                <ListItemIcon>
                  <CopyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('files.copy')}</ListItemText>
              </MenuItem>
            )}
            {contextMenu?.item?.type === 'file' && (
              <MenuItem onClick={() => {
                if (contextMenu?.item) {
                  setReviseDialog({
                    open: true,
                    item: contextMenu.item
                  });
                }
                handleContextMenuClose();
              }}>
                <ListItemIcon>
                  <UploadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('files.revise')}</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={() => {
              if (contextMenu?.item) {
                // TODO: Implement move functionality with dialog
                setMoveDialog({
                  open: true,
                  item: contextMenu.item,
                  targetPath: currentPath // Assuming currentPath is the target path for now
                });
              }
              handleContextMenuClose();
            }}>
              <ListItemIcon>
                <MoveIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('files.move')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              if (contextMenu?.item) {
                setDeleteDialog({
                  open: true,
                  item: contextMenu.item
                });
              }
              handleContextMenuClose();
            }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('files.delete')}</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Rename Dialog */}
      <Dialog open={renameDialog.open} onClose={() => setRenameDialog({ open: false, item: null, newName: '' })}>
        <DialogTitle>{renameDialog.item?.type === 'file' ? t('files.renameFile') : t('files.renameFolder')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('files.newName')}
            variant="outlined"
            value={renameDialog.newName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRenameDialog(prev => ({ ...prev, newName: e.target.value }))}
            onKeyPress={async (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' && renameDialog.item) {
                try {
                  await handleRename(renameDialog.item, renameDialog.newName);
                } catch (error) {
                  console.error('Error renaming item:', error);
                }
              }
            }}
            sx={{
              minWidth: renameDialog.item?.name 
                ? `${Math.max(renameDialog.item.name.length * 8 + 40, 200)}px`
                : '200px',
              width: 'auto'
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialog({ open: false, item: null, newName: '' })}>{t('common.cancel')}</Button>
          <Button 
            onClick={async () => {
              if (renameDialog.item) {
                try {
                  await handleRename(renameDialog.item, renameDialog.newName);
                } catch (error) {
                  console.error('Error renaming item:', error);
                }
              }
            }} 
            variant="contained"
            disabled={!renameDialog.newName.trim()}
          >
            {t('files.rename')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move Dialog */}
      <Dialog open={moveDialog.open} onClose={() => setMoveDialog({ open: false, item: null, targetPath: '' })}>
        <DialogTitle>{moveDialog.item?.type === 'file' ? t('files.moveFile') : t('files.moveFolder')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('files.targetPath')}
            fullWidth
            variant="outlined"
            value={moveDialog.targetPath}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMoveDialog(prev => ({ ...prev, targetPath: e.target.value }))}
            onKeyPress={async (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' && moveDialog.item) {
                try {
                  await handleMoveItem(moveDialog.item, moveDialog.targetPath);
                } catch (error) {
                  console.error('Error moving item:', error);
                }
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDialog({ open: false, item: null, targetPath: '' })}>{t('common.cancel')}</Button>
          <Button 
            onClick={async () => {
              if (moveDialog.item) {
                try {
                  await handleMoveItem(moveDialog.item, moveDialog.targetPath);
                } catch (error) {
                  console.error('Error moving item:', error);
                }
              }
            }} 
            variant="contained"
            disabled={!moveDialog.targetPath.trim()}
          >
            {t('files.move')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Files;